import { useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/services/supabase';
import { useAuthStore } from '@/stores/authStore';
import { apiClient } from '@/services/api/client';
import i18n from '@/i18n';
import { isPortalSubdomain } from '@/utils/portalDetection';
import { tryVarioHandshake } from '@/services/varioHandshake';
import type { User } from '@/types';

/**
 * AuthProvider synchronises Supabase auth state with the Zustand AuthStore.
 *
 * On SIGNED_IN / INITIAL_SESSION it fetches the full user profile
 * (incl. permissions, avatar, is_owner) from the backend /api/auth/me endpoint
 * and stores it.
 * On SIGNED_OUT it clears the store.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { login, logout, setInitialized } = useAuthStore();
  const fetchingRef = useRef(false);
  const lastFetchedTokenRef = useRef<string | null>(null);

  // Guards duerfen erst umleiten, wenn BEIDE asynchronen Wege durch sind:
  // der Vario-Handshake und Supabases erstes Auth-Event. Wer nur auf das
  // Auth-Event wartet, wirft den Vario-Embed raus, weil dessen Session erst
  // durch den Handshake entsteht.
  const handshakeSettledRef = useRef(false);
  const authEventSeenRef = useRef(false);

  const markInitializedWhenReady = useCallback(() => {
    if (handshakeSettledRef.current && authEventSeenRef.current) {
      setInitialized();
    }
  }, [setInitialized]);

  // Vario-Embed: ?appToken= -> Supabase-Session. Loest anschliessend SIGNED_IN
  // aus, das der onAuthStateChange-Listener unten wie ein normaler Login behandelt.
  useEffect(() => {
    void tryVarioHandshake()
      .catch((error) => {
        console.error('[AuthProvider] Vario handshake failed:', error);
      })
      .finally(() => {
        handshakeSettledRef.current = true;
        markInitializedWhenReady();
      });
  }, [markInitializedWhenReady]);

  useEffect(() => {
    const fetchAvatar = async (accessToken: string) => {
      try {
        const res = await apiClient.get('/auth/me/avatar', {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const avatar = res.data?.avatar_base64 ?? null;
        if (avatar) {
          const current = useAuthStore.getState().user;
          if (current) login({ ...current, avatar_base64: avatar }, accessToken);
        }
      } catch {
        // avatar is non-critical
      }
    };

    const fetchAndStoreUser = async (accessToken: string) => {
      if (fetchingRef.current) return;
      if (lastFetchedTokenRef.current === accessToken) return;
      fetchingRef.current = true;

      try {
        const response = await apiClient.get('/auth/me', {
          headers: { Authorization: `Bearer ${accessToken}` },
        });

        const data = response.data;
        const userInfo = data.user || data;
        const existingAvatar = useAuthStore.getState().user?.avatar_base64;

        const user: User = {
          id: userInfo.id,
          email: userInfo.email,
          role: userInfo.role,
          customer_id: userInfo.customer_id,
          first_name: userInfo.first_name,
          last_name: userInfo.last_name,
          is_active: userInfo.is_active,
          is_owner: userInfo.is_owner ?? false,
          permissions: userInfo.permissions ?? {},
          job_title: userInfo.job_title,
          avatar_base64: existingAvatar,
          language_preference: userInfo.language_preference ?? 'de',
          created_at: userInfo.created_at,
          invitation_pending: userInfo.invitation_pending ?? false,
        };

        login(user, accessToken);
        lastFetchedTokenRef.current = accessToken;
        if (!existingAvatar) void fetchAvatar(accessToken);

        // Sync i18n language with user preference so the LanguageDetector
        // cache (localStorage) stays in sync and reloads use the correct language.
        // Skip on portal pages - PortalLayout manages language via ?iso= param.
        const isPortalPage = window.location.pathname.startsWith('/portal/') || isPortalSubdomain();
        if (!isPortalPage && user.language_preference && user.language_preference !== i18n.language) {
          i18n.changeLanguage(user.language_preference);
        }
      } catch (error) {
        console.error('[AuthProvider] Failed to fetch user profile:', error);
        // Clear stale auth state so we don't keep retrying
        logout();
      } finally {
        fetchingRef.current = false;
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (
          (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') &&
          session?.access_token
        ) {
          // Always fetch fresh profile to pick up avatar, permissions, etc.
          await fetchAndStoreUser(session.access_token);
        } else if (event === 'SIGNED_OUT') {
          lastFetchedTokenRef.current = null;
          logout();
        } else if (event === 'TOKEN_REFRESHED' && session?.access_token) {
          const currentUser = useAuthStore.getState().user;
          if (currentUser) {
            login(currentUser, session.access_token);
          }
        }

        authEventSeenRef.current = true;
        markInitializedWhenReady();
      },
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [login, logout, markInitializedWhenReady]);

  return <>{children}</>;
}
